import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Modal, Tooltip, Button, Table } from 'antd'
import { useNotification } from '@/Ship/Hooks/useNotification'
import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { StatusBadge } from './StatusBadge'
import { DelayResolveForm } from './DelayResolveForm'
import { DELAY_STATUS_MAP } from '../Contracts/DelayStatus'
import { TaskDelaysCellStore } from '../Stores/TaskDelaysCellStore'

/**
 * Локальна HEX-мапа кольорів бейджа затримки — лише для цієї комірки.
 * Інші місця (напр. вкладка історії) використовують словесні кольори antd
 * через `<Tag color="orange">` — їхній вигляд не змінюємо.
 * Уніфікувати візуально — лише після погодження UX-зміни з Артемом.
 */
const DELAY_STATUS_HEX_MAP: Record<string, { label: string; color: string }> = {
    received:     { label: DELAY_STATUS_MAP.received.label,     color: '#1890ff' },
    escalated:    { label: DELAY_STATUS_MAP.escalated.label,    color: '#ff4d4f' },
    resolved:     { label: DELAY_STATUS_MAP.resolved.label,     color: '#52c41a' },
    not_resolved: { label: DELAY_STATUS_MAP.not_resolved.label, color: '#d4380d' },
}

const formatDatetime = (value: string | null | undefined) => {
    if (!value) return '—'
    return new Date(value).toLocaleString('uk-UA', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
}

interface Delay {
    uuid: string
    status: string
    driverComment: string
    receivedAt: string
    escalatedAt?: string | null
    resolvedAt?: string | null
    resolverComment?: string | null
    resolverDispatcher?: { fullName?: string; title?: string } | null
}

interface Props {
    delays: Delay[]
    /** Kept for backwards-compat with consumers; the cell no longer reads it. */
    taskUuid?: string
    onResolved?: () => void
}

export const TaskDelaysCell = observer(({ delays: initialDelays, onResolved }: Props) => {
    const [store] = useState(() => new TaskDelaysCellStore())
    const { showSuccess, showError } = useNotification()
    const crudContext = useCrudResourceContext()

    useEffect(() => {
        store.syncFromProps(initialDelays)
    }, [store, initialDelays])

    if (!store.delays.length) return <span>—</span>

    const latest = store.delays[0]
    if (!latest) return <span>—</span>

    const notify = { success: showSuccess, error: showError }
    const reload = () => {
        crudContext?.actions?.reloadData?.()
        onResolved?.()
    }

    const tooltipContent = (
        <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                    <th style={{ padding: '2px 8px', borderBottom: '1px solid rgba(255,255,255,0.2)', textAlign: 'left' }}>Дата затримки</th>
                    <th style={{ padding: '2px 8px', borderBottom: '1px solid rgba(255,255,255,0.2)', textAlign: 'left' }}>Статус</th>
                </tr>
            </thead>
            <tbody>
                {store.delays.map((d) => (
                    <tr key={d.uuid}>
                        <td style={{ padding: '2px 8px' }}>{formatDatetime(d.receivedAt)}</td>
                        <td style={{ padding: '2px 8px' }}>{DELAY_STATUS_MAP[d.status as keyof typeof DELAY_STATUS_MAP]?.label ?? d.status}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const columns = [
        {
            title: 'Дата отримання',
            key: 'receivedAt',
            width: 150,
            render: (_: any, d: Delay) => formatDatetime(d.receivedAt),
        },
        {
            title: 'Коментар водія',
            key: 'driverComment',
            render: (_: any, d: Delay) => d.driverComment || '—',
        },
        {
            title: 'Статус',
            key: 'status',
            width: 130,
            render: (_: any, d: Delay) => <StatusBadge value={d.status} map={DELAY_STATUS_HEX_MAP} />,
        },
        {
            title: 'Дата ескалації',
            key: 'escalatedAt',
            width: 150,
            render: (_: any, d: Delay) => formatDatetime(d.escalatedAt),
        },
        {
            title: 'Дата вирішення',
            key: 'resolvedAt',
            width: 150,
            render: (_: any, d: Delay) => formatDatetime(d.resolvedAt),
        },
        {
            title: 'Коментар диспетчера',
            key: 'resolverComment',
            render: (_: any, d: Delay) => d.resolverComment || '—',
        },
        {
            title: 'Дії',
            key: 'actions',
            width: 160,
            render: (_: any, d: Delay) => {
                if (!store.canResolve(d.status)) return null

                if (store.resolvingUuid === d.uuid) {
                    return (
                        <DelayResolveForm
                            loading={store.loading}
                            onConfirm={(comment, outcome) => store.resolve(d.uuid, comment, notify, reload, outcome)}
                            onCancel={() => store.cancelResolving()}
                        />
                    )
                }

                return (
                    <Button
                        size="small"
                        type="primary"
                        onClick={() => store.startResolving(d.uuid)}
                    >
                        Результат
                    </Button>
                )
            },
        },
    ]

    return (
        <>
            <Tooltip title={tooltipContent} color="#1f1f1f" mouseEnterDelay={0.3}>
                <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => store.setOpen(true)}
                >
                    <StatusBadge value={latest.status} map={DELAY_STATUS_HEX_MAP} />
                </span>
            </Tooltip>

            <Modal
                title="Затримки виконання завдання"
                open={store.open}
                onCancel={() => store.setOpen(false)}
                footer={null}
                width={900}
            >
                <Table
                    dataSource={store.delays.map((d) => ({ ...d, key: d.uuid }))}
                    columns={columns}
                    pagination={false}
                    size="small"
                    bordered
                    scroll={{ x: 'max-content' }}
                />
            </Modal>
        </>
    )
})
